import { useState, useEffect } from 'react';
import { useId } from '~/common/use-id';

export function useLocalStorage(key, initialValue) {
  const storageId = useId();
  const [storedValue, setStoredValue] = useState(() => {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : initialValue;
  });

  const setValue = (value) => {
    const _value = value instanceof Function ? value(storedValue) : value;

    setStoredValue(_value);

    if (_value === undefined) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(_value));
    }

    // Dispatch custom event to notify other components about localStorage change
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: {
        key,
        value: _value,
        storageId,
      },
    }));
  };

  // Listen for storage changes from other components
  useEffect(() => {
    const handleCustomStorageChange = (e) => {
      if (e.detail?.key !== key) {
        return;
      }
      if (e.detail?.storageId === storageId) {
        return;
      }

      const newValue = e.detail.value !== undefined ? e.detail.value : initialValue;
      setStoredValue(newValue);
    };

    window.addEventListener('localStorageChange', handleCustomStorageChange);

    return () => {
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, storageId]); // Intentionally omitting initialValue - it's only needed for initial state, not listener setup. Including it causes re-renders on every component render since it's often a new array/object literal.

  return [storedValue, setValue];
}

export function useSessionStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    const item = window.sessionStorage.getItem(key);
    return item ? JSON.parse(item) : initialValue;
  });

  const setValue = (value) => {
    const _value = value instanceof Function ? value(storedValue) : value;
    setStoredValue(_value);
    if (_value === undefined) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(_value));
    }
  };

  return [storedValue, setValue];
}
